import { StateContext } from '@ngxs/store';
import { IActionWithPayload } from 'app/projects/core/src/lib/interfaces/action_with_payload.interface';
import { MaxBrainUtils } from 'app/projects/core/src/lib/utils';
import { IEntity } from 'app/projects/entity/src/lib/interfaces/entity.interface';
import { Observable } from 'rxjs';
import { IAddSubentitiesToEntityActionPayload } from '../interfaces/add-subentities-to-entity.action-payload.interface';
import { IRemoveSubentityFromEntityActionPayload } from '../interfaces/remove-subentity-from-entity.action-payload.interface';
import { ISubentityService } from '../interfaces/subentity.service.interface';
import { SubentityStateModel } from './subentity.state-model';

export abstract class SubentityState<T extends IEntity> {
    private _expirationTime = {};
    protected _propstoSearch = null;

    constructor(protected _subentityService?: ISubentityService<T>, private _timeoutDuration: number = 0) {
        if (this._timeoutDuration < 0) {
            this._timeoutDuration = 0;
        }
    }

    /**
     *
     * @param key
     */
    private setExpirationTime(key): void {
        this._expirationTime[key] = this._timeoutDuration * 1000 + Date.now();
    }

    /**
     *
     * @param subentities
     * @param searchString
     */
    protected _filterSubentitiesByStringAndMapToIds(subentities: T[], searchString: string): string[] {
        return MaxBrainUtils.filterArrayByString(subentities, searchString, this._propstoSearch).map((subentity) => subentity.id);
    }

    /**
     *
     * @param getState
     * @param patchState
     * @param payload
     * @param subentities
     */
    protected _patchSubentities<M extends SubentityStateModel>(
        { getState, patchState }: StateContext<M>,
        { payload }: IActionWithPayload<{ subentityIds: string[] }>,
        subentities: T[]
    ): void {
        const state = getState();
        const list = [...(state.list || []), ...payload.subentityIds].filter((value, index, self) => self.indexOf(value) === index);
        const filtered = this._filterSubentitiesByStringAndMapToIds(
            subentities.filter((subentity) => list.includes(subentity.id)),
            state.searchTerm
        );

        patchState({
            list,
            filtered,
        } as Partial<M>);
    }

    /**
     *
     * @param ctx
     * @param action
     * @param subentities
     * @param SuccessAction
     * @param FailureAction
     * @param serviceMethod
     */
    protected _addSubentities<M extends SubentityStateModel>(
        ctx: StateContext<M>,
        action: IActionWithPayload<IAddSubentitiesToEntityActionPayload>,
        subentities: T[],
        SuccessAction: new () => any,
        FailureAction: new () => any,
        serviceMethod: Observable<any> = this._subentityService ? this._subentityService.add(action.payload.entityId, action.payload.subentityIds) : null
    ): void {
        if (!serviceMethod) {
            return;
        }

        serviceMethod.subscribe(
            () => {
                this._patchSubentities(ctx, action, subentities);
                setTimeout(() => ctx.dispatch(new SuccessAction()));
            },
            (error) => {
                console.error(error);
                ctx.dispatch(new FailureAction());
            }
        );
    }

    /**
     *
     * @param getState
     * @param patchState
     * @param dispatch
     * @param payload
     * @param SuccessAction
     * @param FailureAction
     * @param serviceMethod
     */
    protected _removeSubentity<M extends SubentityStateModel>(
        { getState, patchState, dispatch }: StateContext<M>,
        { payload }: IActionWithPayload<IRemoveSubentityFromEntityActionPayload>,
        SuccessAction: new (payload: any) => any,
        FailureAction: new (payload: any) => any,
        serviceMethod: Observable<any> = this._subentityService ? this._subentityService.remove(payload.entityId, payload.subentityId) : null
    ): void {
        if (!serviceMethod) return;

        serviceMethod.subscribe(
            () => {
                const state = getState();
                patchState({
                    list: [...state.list.filter((subentityId) => subentityId !== payload.subentityId)],
                    selected: [...state.selected.filter((subentityId) => subentityId !== payload.subentityId)],
                    filtered: [...state.filtered.filter((subentityId) => subentityId !== payload.subentityId)],
                } as Partial<M>);
                setTimeout(() => dispatch(new SuccessAction(payload)));
            },
            (error) => {
                console.error(error);
                setTimeout(() => dispatch(new FailureAction(payload)));
            }
        );
    }

    /**
     *
     * @param getState
     * @param patchState
     * @param dispatch
     * @param payload
     * @param AddEntities
     */
    protected _setSubentities<M extends SubentityStateModel>(
        { getState, patchState, dispatch }: StateContext<M>,
        { payload }: IActionWithPayload<T[]>,
        AddEntities: any = null
    ): void {
        const subentities = [...payload];
        if (AddEntities) dispatch(new AddEntities(subentities));

        const state = getState();
        const list = subentities.map((subentity) => subentity.id);
        const filtered = this._filterSubentitiesByStringAndMapToIds(subentities, state.searchTerm);

        patchState({
            list,
            filtered,
            selected: [],
        } as Partial<M>);
    }

    /**
     *
     * @param patchState
     */
    protected _unsetSubentities<M extends SubentityStateModel>({ patchState }: StateContext<M>): void {
        patchState({
            list: null,
            filtered: [],
            selected: [],
        } as Partial<M>);
    }

    /**
     *
     * @param getState
     * @param patchState
     * @param dispatch
     * @param payload
     * @param SetSubentitiesAction
     * @param SuccessAction
     * @param FailureAction
     * @param serviceMethod
     */
    protected _fetchSubentities<M extends SubentityStateModel, R>(
        { getState, patchState, dispatch }: StateContext<M>,
        { payload }: IActionWithPayload<string>,
        SetSubentitiesAction: new (subentities: R) => any,
        SuccessAction: new (payload?: any) => any,
        FailureAction: new (payload?: any) => any,
        serviceMethod: Observable<R> = this._subentityService ? (this._subentityService.getAll(payload) as unknown as Observable<R>) : null
    ): void {
        if (!serviceMethod) return;

        const state = getState();
        if (state.fetchingList) return;

        patchState({ fetchingList: true } as Partial<M>);

        if ((this._expirationTime as number) > Date.now() && state.list) {
            setTimeout(() => dispatch(new SuccessAction()));
            return;
        }

        serviceMethod.subscribe(
            (result) => {
                setTimeout(() => dispatch(new SetSubentitiesAction(result)));
                setTimeout(() => dispatch(new SuccessAction(result)));
            },
            (error) => {
                console.error(error);
                setTimeout(() => dispatch(new FailureAction(error)));
            },
            () => {
                this.setExpirationTime(payload);
            }
        );
    }

    /**
     *
     * @param getState
     * @param patchState
     * @param entityId
     */
    protected _removeSubentityFromState<M extends SubentityStateModel>({ getState, patchState }: StateContext<M>, entityId: string): void {
        const state = getState();
        patchState({
            list: state.list?.filter((id) => id !== entityId),
            selected: state.selected?.filter((id) => id !== entityId),
            filtered: state.filtered?.filter((id) => id !== entityId),
        } as Partial<M>);
    }

    /**
     *
     * @param patchState
     */
    protected _fetchSubentitiesSuccess<M extends SubentityStateModel>({ patchState }: StateContext<M>): void {
        patchState({ fetchingList: false } as Partial<M>);
    }

    /**
     *
     * @param patchState
     */
    protected _fetchSubentitiesFailure<M extends SubentityStateModel>({ patchState }: StateContext<M>): void {
        patchState({ fetchingList: false } as Partial<M>);
    }

    /**
     *
     * @param getState
     * @param patchState
     * @param payload
     */
    protected _toggleSelectedSubentity<M extends SubentityStateModel>({ getState, patchState }: StateContext<M>, { payload }: IActionWithPayload<string>): void {
        const state = getState();
        patchState({
            selected: state.selected.indexOf(payload) === -1 ? [...state.selected, payload] : [...state.selected.filter((id) => id !== payload)],
        } as Partial<M>);
    }

    /**
     *
     * @param getState
     * @param patchState
     */
    protected _selectFilteredSubentities<M extends SubentityStateModel>({ getState, patchState }: StateContext<M>): void {
        const state = getState();
        patchState({
            selected: state.filtered ? [...state.filtered] : [],
        } as Partial<M>);
    }

    /**
     *
     * @param patchState
     */
    protected _deselectSelectedSubentities<M extends SubentityStateModel>({ patchState }: StateContext<M>): void {
        patchState({ selected: [] } as Partial<M>);
    }

    /**
     *
     * @param getState
     * @param patchState
     * @param payload
     * @param subentities
     */
    protected _searchSubentities<M extends SubentityStateModel>({ getState, patchState }: StateContext<M>, { payload }: IActionWithPayload<string>, subentities: T[]): void {
        const state = getState();
        const filtered = this._filterSubentitiesByStringAndMapToIds(
            subentities.filter((subentity) => state.list.includes(subentity.id)),
            payload
        );
        patchState({ searchTerm: payload, filtered } as Partial<M>);
    }
}
